import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, TrendingUp, Phone, BarChart3, Bot, Zap, Settings, Globe, Server } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const Services = () => {
  const services = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Timeshare OPC Marketing",
      description: "Our expert OPC marketers engage vacationers with discounted theme park tickets and resort experiences, driving qualified tours to your property. We maximize savings for guests while maximizing conversions for you.",
      features: ["Qualified Tour Generation", "Theme Park Ticket Sales", "Concierge Services", "Resort Bookings"],
      color: "bg-[#1E3A8A]",
      highlight: true,
      link: "/timeshare-opc"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Lead Generation & Business Development",
      description: "Ascend the digital throne with our SEO dream team and social media marketing that rocks. From keyword alchemy to viral content engineering, we drive unprecedented growth and success for your business.",
      features: ["SEO Domination", "Social Media Marketing", "Email Campaigns", "PPC Advertising"],
      color: "bg-[#0D9488]",
      highlight: true,
      link: "/lead-generation"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Marketing Partnerships",
      description: "We build powerful relationships with local businesses and communities to expand your reach and create mutually beneficial marketing opportunities.",
      features: ["Local Partnerships", "Co-Marketing Campaigns", "Community Events", "Brand Ambassadors"],
      color: "bg-[#EA580C]",
      link: "/services"
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Call Center & Appointment Setting",
      description: "Professional outreach services that connect you with prospects and set qualified appointments. Our team represents your brand with excellence.",
      features: ["Outbound Campaigns", "Appointment Scheduling", "Follow-up Calls", "Quality Monitoring"],
      color: "bg-[#DC2626]",
      link: "/services"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Digital Advertising",
      description: "Comprehensive digital advertising across Google, social media, and Connected TV (CTV). We optimize every campaign for maximum ROI and reach.",
      features: ["Google Ads", "Social Media Ads", "Connected TV (CTV)", "Retargeting Campaigns"],
      color: "bg-[#1E3A8A]",
      link: "/services"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "CRM & Automation",
      description: "Streamlined customer relationship management with powerful automation. We help you manage leads, track interactions, and automate follow-ups.",
      features: ["CRM Setup", "Workflow Automation", "Email Sequences", "Performance Tracking"],
      color: "bg-[#0D9488]",
      link: "/services"
    },
    {
      icon: <Bot className="w-8 h-8" />,
      title: "AI Tools",
      description: "Cutting-edge AI solutions including our flagship Ghost Assistant. Leverage artificial intelligence to enhance customer engagement and streamline operations.",
      features: ["Ghost AI Assistant", "Chatbot Integration", "AI Analytics", "Predictive Insights"],
      color: "bg-[#EA580C]",
      link: "/services"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Web Design & Hosting",
      description: "World-class website design and enterprise-grade hosting powered by cutting-edge technology. We build stunning, high-performance websites that convert visitors into customers using AI-assisted development and cloud infrastructure.",
      features: ["AI-Powered Design", "Enterprise Cloud Hosting", "99.99% Uptime SLA", "Advanced Security"],
      color: "bg-[#1E3A8A]",
      highlight: true,
      link: "/services"
    }
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
            Our Services
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6 max-w-3xl mx-auto">
            Marketing Solutions That{' '}
            <span className="text-gradient">Drive Results</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From timeshare OPC marketing to cutting-edge AI tools, we deliver comprehensive 
            solutions tailored to accelerate your business growth.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white" data-testid="services-grid">
        <div className="container-max mx-auto">
          <div className="space-y-8">
            {services.map((service, idx) => (
              <Card 
                key={idx} 
                className={`border-slate-200 overflow-hidden ${service.highlight ? 'ring-2 ring-[#1E3A8A]' : ''}`}
                data-testid={`service-detail-${idx}`}
              >
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-3 gap-0">
                    <div className={`${service.color} p-8 lg:p-12 flex flex-col justify-center`}>
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 text-white">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {service.title}
                      </h3>
                      {service.highlight && (
                        <span className="inline-block bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full w-fit">
                          Primary Service
                        </span>
                      )}
                    </div>
                    <div className="lg:col-span-2 p-8 lg:p-12">
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3 mb-8">
                        {service.features.map((feature, fidx) => (
                          <div key={fidx} className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-[#EA580C]" />
                            <span className="text-slate-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <Link to={service.link}>
                        <Button 
                          className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                          data-testid={`learn-more-${idx}`}
                        >
                          Learn More
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              How We Deliver Results
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", desc: "We learn about your business, goals, and challenges." },
              { step: "02", title: "Strategy", desc: "We craft a custom marketing strategy tailored to you." },
              { step: "03", title: "Execute", desc: "We implement campaigns with precision and expertise." },
              { step: "04", title: "Optimize", desc: "We continuously refine for maximum performance." }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-black text-[#1E3A8A]/10 mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            Ready to Transform Your Marketing?
          </h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how our services can help you achieve your business goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="services-cta-partner"
              >
                Become a Partner
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8"
                data-testid="services-cta-contact"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
